import {
  List,
  Datagrid,
  ExportButton,
  TopToolbar,
  FunctionField,
  WrapperField,
} from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { RaRowActions } from "../../components/RaRowActions";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { determineUserReadableRole } from "../users/UserUtils";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";
import { mailTriggers, TriggerChip } from "./mail-templates-util";

// Estilo compacto de tabela — espelho do datagridSx das demais listas
const datagridSx = {
  "& .RaDatagrid-headerCell": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
};

export const MailTemplateList = () => {
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";

  const ListActions = () => (
    <TopToolbar sx={{ alignItems: "center" }}>
      <ExportButton />
      {/* navegação ancorada à direita; botões ficam à esquerda */}
      <span style={{ marginLeft: "auto", display: "flex" }}>
        <ListPageIndicator />
      </span>
    </TopToolbar>
  );

  return (
    <>
      <PageTopBreadcrumb
        title="Templates de E-mail"
        firstPath="/#/mail-templates/crud"
        actionType="create"
      />
      <List
        title=" "
        // Sem busca de propósito: são poucos templates fixos — mesma
        // decisão das telas de Categorias e Configurações de Sistema
        actions={<ListActions />}
        perPage={25}
        queryOptions={{ keepPreviousData: true }}
        sx={{
          // Mesmo padrão visual das demais listas do painel
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "12px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            minHeight: "auto !important",
            paddingBottom: 1,
          },
        }}
      >
        <Datagrid rowClick="show" sx={datagridSx} bulkActionButtons={false}>
          <FunctionField
            label="Nome"
            sortBy="name"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: colors.primaryDark,
                }}
              >
                {record.name || "—"}
              </Typography>
            )}
          />
          {/* Nome amigável do gatilho + código técnico; gatilho fora da
              lista real ganha o aviso de que o servidor não o dispara */}
          <FunctionField
            label="Gatilho"
            sortBy="trigger"
            render={(record: any) => (
              <Box>
                <TriggerChip trigger={record.trigger} />
                <Typography
                  sx={{
                    fontSize: 11,
                    marginTop: 0.25,
                    color: colors.textMuted,
                    ...(mailTriggers[record.trigger]
                      ? { fontFamily: fontFamilyMono }
                      : {}),
                  }}
                >
                  {mailTriggers[record.trigger]
                    ? record.trigger
                    : "sem disparo automático no servidor"}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label="Assunto"
            sortBy="subject"
            render={(record: any) => (
              <Typography
                component="span"
                title={record.subject || ""}
                sx={{
                  fontSize: 13,
                  color: colors.textMuted,
                  display: "block",
                  maxWidth: 340,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.subject || "—"}
              </Typography>
            )}
          />
          {/* Ações visíveis no lugar do menu ⋮ */}
          <WrapperField label="Ações" textAlign="right">
            <RaRowActions show edit={isAdmin} del={isAdmin} />
          </WrapperField>
        </Datagrid>
      </List>
      {/* Quadro informativo com os gatilhos REAIS — a versão antiga
          listava dois gatilhos que não existem no código do servidor */}
      <Box
        sx={{
          backgroundColor: colors.bgSubtle,
          border: `1px solid ${colors.border}`,
          borderRadius: `${radius.md}px`,
          padding: "14px 18px",
          marginTop: 2,
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: 0.75,
            color: colors.primary,
          }}
        >
          <HiOutlineInformationCircle size={18} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: colors.textStrong }}
          >
            Como funcionam os disparos automáticos
          </Typography>
        </Box>
        <Box
          component="ul"
          sx={{
            margin: 0,
            paddingLeft: "18px",
            color: colors.textMuted,
            fontSize: 12.5,
            "& li": { marginBottom: "3px" },
            "& code": { fontFamily: fontFamilyMono, fontSize: 11.5 },
          }}
        >
          <li>
            <b>Boas-vindas ao sócio</b> (<code>NEW_ACCOUNT_MEMBER</code>) — vai
            para o e-mail do sócio quando a conta recebe a matrícula;
          </li>
          <li>
            <b>Aviso de conta nova</b> (<code>NEW_ACCOUNT_MEMBER_ADMIN</code>)
            — vai para o e-mail da administração a cada conta criada, com o
            nome e o e-mail de quem se cadastrou anexados ao final do corpo;
          </li>
          <li>
            <b>Recuperação de senha</b> (<code>ACCOUNT_RECOVERY</code>) — vai
            para o sócio que pediu "esqueci a senha"; o link de recuperação é
            anexado ao final do corpo automaticamente;
          </li>
          <li>
            <b>Novo contato recebido</b> (<code>NEW_CONTACT_MESSAGE</code>) —
            vai para o e-mail da administração quando chega mensagem pelo
            formulário de Contato.
          </li>
        </Box>
        <Typography
          sx={{ fontSize: 12.5, color: colors.textMuted, marginTop: 0.75 }}
        >
          Cada gatilho aceita <b>um único template</b>. Templates com outros
          gatilhos não são enviados automaticamente — mas qualquer template
          pode ser usado no <b>envio manual em massa</b> (Sócios › Iniciar
          Operações em Massa).
        </Typography>
      </Box>
    </>
  );
};
